import { useTranslation } from "react-i18next";
import PanelTitle from "../common/components/PanelTitle";
import { useRequestableJob } from "../reducers/requestableJobsSlice";
import DefaultButton from "../common/components/DefaultButton";
import Card from "../common/components/Card";
import { requestJobAccess, upateJobAccessRequest, useJobAccessRequests } from "../reducers/jobAccessRequestsSlice";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import DataTable from "../common/components/DataTable";
import useClientConfig from "../common/hooks/clientConfigHook";
import CustomForm from "../common/components/CustomForm";
import { Col, Row } from "../common/components/Grid";
import ReactForm from "../common/components/ReactForm";
import useNetwork from "../common/hooks/networkHook";
import { deepMerge } from "../common/utils/objectUtilities";
import { ReactNodeDialog } from "../common/utils/dialogUtlities";
import useCredentials from "../common/hooks/credentialsHook";
import { canUpdateJob } from "../reducers/jobSlice";
import DateTimeInput from "../common/components/DateTimeInput";
import { nowISOString } from "../common/utils/dateUtils";
import SiteDropdown from "./SiteDropdown";
import LevelDropdown from "./LevelDropdown";
import TypeDropdown from "./TypeDropdown";
import { distinct } from "../common/utils/arrayUtilities";
import { shallowEqual } from "react-redux";

export const JobRequestContext = createContext({ });

export const JobRequestProvider = ({ children, job }) => {
    const [ requestData, setRequestData ] = useState({});
    const network = useNetwork();

    const save = useCallback(() => {
        requestJobAccess(network, {...requestData, jobid: job.jobid});
        setRequestData({})
    }, [job.jobid, network, requestData]);

    const onChange = useCallback((delta) => {
        setRequestData(prev => deepMerge({}, prev, delta));
    }, []);

    const value = useMemo(() => ({ requestData,  onChange, save }), [requestData, onChange, save]);
    
    return <JobRequestContext.Provider value={value}>
        {children}
    </JobRequestContext.Provider>
}

const AddJobAccessRequestButton = ({isValid}) => {
    const { t } = useTranslation();
    const { save } = useContext(JobRequestContext);

    return <DefaultButton className="ml-4" kind="add" label={t('submit')} onClick={() => {isValid() && save()}}></DefaultButton>
}

const JobDecisionFormDialog = ({show, request, disabled, handleClose, handleSave}) => {
    const [ decisionData, setDecisionData ] = useState();
    const clientConfig = useClientConfig();
    const { t } = useTranslation();
    const formRef = useRef();
    
    useEffect(() => setDecisionData(prev => request?.decision_customfields), [request])

    const onChange = useCallback((delta) => {       
        setDecisionData(prev => deepMerge({}, prev, delta));
    }, []);

    return <ReactNodeDialog title={t('requestDecisionDetails')} showModal={!!show} onClose={handleClose} invalid={!formRef?.current?.checkValidity()} onSave={!disabled && !request?.decision_username ? () => handleSave(decisionData) : undefined}>
        <CustomForm def={clientConfig.requestableJobs.jobAccessRequestFormSpec} data={request} readonly></CustomForm>
            <ReactForm ref={formRef}>
                <CustomForm disabled={disabled ||request?.decision_username} def={request?.accepted_timestamp ? clientConfig.requestableJobs.jobAccessApproveFormSpec : clientConfig.requestableJobs.jobAccessRejectFormSpec} data={decisionData} onChange={onChange} required={true}></CustomForm>
            </ReactForm>
        </ReactNodeDialog>
}

const JobRequestDetails = ({ job, assistance = [] }) => {
    const { requestData, onChange } = useContext(JobRequestContext);
    const clientConfig = useClientConfig();

    const typeFilter = useCallback((t) => job.type !== t.type && assistance.every(a => a.type !== t.type), [assistance, job.type]);
    return <>
        <Row>
            <Col xs style={{ position: "relative" }}>
                { clientConfig.enableSites && <Row>
                    <Col>
                        <SiteDropdown {...requestData} perm="read" level={requestData.assistance?.level} type={requestData.assistance?.type} onChange={assistance => onChange({assistance})}></SiteDropdown>
                    </Col>
                </Row> }
                { clientConfig.enableLevels && <Row>
                    <Col>
                        <LevelDropdown {...requestData} perm="read" site={requestData.assistance?.site} type={requestData.assistance?.type} onChange={assistance => onChange({assistance})}></LevelDropdown>
                    </Col>
                </Row> }
                <Row>
                    <Col>
                        <TypeDropdown {...requestData} perm="read" level={requestData.assistance?.level} site={requestData.assistance?.site} onChange={t => onChange({assistance: {type: t.type}})} filter={typeFilter}></TypeDropdown>
                    </Col>
                </Row>
            </Col>
        </Row>
        <CustomForm def={clientConfig.requestableJobs.jobAccessRequestFormSpec} data={requestData} onChange={onChange} required={true}></CustomForm>
    </>
}

const JobRequestsCard = ({jobid, headerControls, canUpdate = false}) => {
    const [ pendingDecision, setPendingDecision ] = useState();
    const jobAccessRequests = useJobAccessRequests(jobid);    
    const clientConfig = useClientConfig();
    const { t } = useTranslation();
    const network = useNetwork();

    const jobRequestColumns = useMemo(() => ([
        { title: t('username'), type: 'text', field: 'username', editable: false, readonly: true },
        { title: t('type_one'), type: 'text', field: 'assistance.type', editable: false, readonly: true },
        { title: t('site_one'), type: 'text', field: 'assistance.site', editable: false, readonly: true },
        { title: t('level_one'), type: 'text', field: 'assistance.level', editable: false, readonly: true },
        { title: t('created'), type: 'datetime', field: 'created', editable: false, readonly: true, showTZ: false},
        { title: t('status_one'), type: 'dynamic', def: row => { 
            if (row.accepted_timestamp) {
                return <span>{t('requestApproved')}</span>;
            } else if (row.rejected_timestamp) {
                return <span>{t('requestDenied')}</span>;
            } else {
                return <span>{t('pending')}</span>
            }
        }},
        { title: t('decisionTimeStamp'), type: 'dynamic', def: row => <DateTimeInput value={row.accepted_timestamp || row.rejected_timestamp} readonly showTZ={false} editable={false}></DateTimeInput>},
        { title: t('decisionUsername'), type: 'text', field: 'decision_username', editable: false, readonly: true },
        ]), [t]);

        const customCols = useMemo(() => (clientConfig.requestableJobs.jobAccessRequestFormSpec ?? []).flatMap(s => 
            s.rows.flatMap(row => row.map(({ label, ...rest }) => ({ ...rest, title: label })))
        ), [clientConfig]);

        const approveRequest = useCallback(({decision_customfields, jobid, internalid, accepted_timestamp}) => {
            upateJobAccessRequest(network, {decision_customfields, accepted_timestamp, jobid, internalid})
        }, [network]);

        const rejectRequest = useCallback(({decision_customfields, jobid, internalid, rejected_timestamp}) => {
            upateJobAccessRequest(network, {decision_customfields, rejected_timestamp, jobid, internalid})
        }, [network]);

        const handleApprove = useCallback((request) => {
            if (clientConfig.requestableJobs.jobAccessApproveFormSpec) {
                setPendingDecision({...request, accepted_timestamp: nowISOString(), decision_customfields: {}});
            } else {
                approveRequest({...request, accepted_timestamp: nowISOString(), decision_customfields: {}});
                setPendingDecision();
            }
        }, [approveRequest, clientConfig]);

        const handleReject = useCallback((request) => {     
            if (clientConfig.requestableJobs.jobAccessRejectFormSpec) {
                setPendingDecision({...request, rejected_timestamp: nowISOString(), decision_customfields: {}});
            } else {
                rejectRequest({...request, rejected_timestamp: nowISOString(), decision_customfields: {}});
                setPendingDecision();
            }
        }, [clientConfig, rejectRequest]);

        const handleDecision = useCallback((decisionDetails) => {
            if (pendingDecision.accepted_timestamp) {
                approveRequest({...pendingDecision, decision_customfields: decisionDetails});
                setPendingDecision();
            } else {                
                rejectRequest({...pendingDecision, decision_customfields: decisionDetails});
                setPendingDecision();
            }
        }, [approveRequest, pendingDecision, rejectRequest]);

        const rowControls = useCallback((row) => {
            if (!(row.accepted_timestamp || row.rejected_timestamp) && canUpdate) {
                return <div className="d-flex flex-row" style={{ gap: '4px' }}>
                    <DefaultButton kind="apply" hideLabel onClick={() => handleApprove(row)}></DefaultButton> 
                    <DefaultButton kind="close" variant="danger" hideLabel onClick={() => handleReject(row)}></DefaultButton>
                </div>
            } else if ((row.accepted_timestamp || row.rejected_timestamp) && ((clientConfig.requestableJobs.jobAccessApproveFormSpec && row?.decision_customfields) || (clientConfig.requestableJobs.jobAccessRejectFormSpec && row?.decision_customfields))) {
                return <DefaultButton kind="view" onClick={() => setPendingDecision(row)}></DefaultButton>
            }
            return
        }, [canUpdate, clientConfig, handleApprove, handleReject])

    return <>
        {(clientConfig.requestableJobs.jobAccessApproveFormSpec || clientConfig.requestableJobs.jobAccessRejectFormSpec) && 
           <JobDecisionFormDialog show={!!pendingDecision} disabled={!canUpdate} handleClose={() => setPendingDecision()} request={pendingDecision} handleSave={handleDecision}></JobDecisionFormDialog> 
        }
        <DataTable columns={jobRequestColumns.concat(customCols)} value={jobAccessRequests} disableAdd disableRemove defaultSort={['-created']} emptyMessage={t('none')} rowControls={rowControls}>
        </DataTable>
    </>
}

export const AdminJobRequestsCard = ({jobid, assistance, canUpdate}) => {
    const [ showRequestDialog, setShowRequestDialog ] = useState();
    const { save } = useContext(JobRequestContext);    
    const clientConfig = useClientConfig();
    const job = useRequestableJob(jobid);
    const credentials = useCredentials();
    const formRef = useRef();

     const canAddNewRequests = useMemo(() => {
        const accessFull = credentials.access.accessTriples(clientConfig, 'full');
        const accessRead = credentials.access.accessTriples(clientConfig, 'read');
        const userTriplets = distinct((accessFull ??[]).concat(accessRead));

        const usedTriplets = (assistance ?? []).concat(job ?? []).map(t => ({ type: t.type, site: t.site, level: t.level }));
        const remainingSelections = userTriplets.filter(t => !usedTriplets.some(a => shallowEqual(a, t)));
        
        return !canUpdateJob(credentials, clientConfig, job, assistance) && remainingSelections.length > 0
    }, [credentials, clientConfig, job, assistance]);

    const handleSave = useCallback(() => {
        if (formRef.current.checkValidity()){
            save();
            setShowRequestDialog(false)
        }
    }, [save])

    const headerControls = canAddNewRequests && <DefaultButton className="ml-4" kind="add" onClick={() => setShowRequestDialog(true)}></DefaultButton>

    return <>
        <ReactNodeDialog showModal={showRequestDialog} onClose={setShowRequestDialog} onSave={handleSave}>
            <ReactForm ref={formRef}><JobRequestDetails job={job} assistance={assistance}></JobRequestDetails></ReactForm>
        </ReactNodeDialog>
        <div className="d-flex flex-column">
            { headerControls && <div className="text-end">
                {headerControls}
            </div> }
            <div style={{ flex: 1 }}>
                <JobRequestsCard canUpdate={canUpdate} jobid={jobid} headerControls={headerControls}></JobRequestsCard>
            </div>
        </div>
    </>
}

const JobRequestPanel = ({ requestableJobId, onPin, onUnpin, onPopout, onClose }) => {
    const job = useRequestableJob(requestableJobId);
    const clientConfig = useClientConfig();
    const { t } = useTranslation();
    const formRef = useRef();
 
    return job && <div id="jobRequestPanel">
        <JobRequestProvider job={job}>
            <PanelTitle title={t('jobAccessRequests', {id: job.displayid })} onPin={onPin} onUnpin={onUnpin} onPopout={onPopout}>
                <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'end', rowGap: '10px' }}>
                    <DefaultButton className="ml-4" kind="close" onClick={onClose}></DefaultButton>
                    <AddJobAccessRequestButton isValid={() => formRef.current.checkValidity()}></AddJobAccessRequestButton>
                </div>
            </PanelTitle>
            <Row><Col>
                <CustomForm def={clientConfig.requestableJobs.displayFields} data={job ?? {}} disabled={true} readonly={true}></CustomForm>
            </Col></Row>
            <ReactForm ref={formRef}>
                <JobRequestDetails job={job}></JobRequestDetails>
            </ReactForm>
            <Card title={t('submittedJobAccessRequests')}>
                <JobRequestsCard jobid={job.jobid}></JobRequestsCard>
            </Card>
        </JobRequestProvider>
    </div>
}

export default JobRequestPanel;